import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  Slider,
  Box,
  Typography,
  Stack,
} from '@mui/material';
import Cropper from 'react-easy-crop';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';

const PhotoCropModal = ({ open, onClose, imageUrl, onSave }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const createCroppedImage = async () => {
    try {
      const image = await createImage(imageUrl);
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      canvas.width = croppedAreaPixels.width;
      canvas.height = croppedAreaPixels.height;

      ctx.drawImage(
        image,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0,
        0,
        croppedAreaPixels.width,
        croppedAreaPixels.height
      );

      return new Promise((resolve) => {
        canvas.toBlob(
          (blob) => {
            resolve(blob);
          },
          'image/jpeg',
          0.95
        );
      });
    } catch (error) {
      console.error('Erro ao recortar imagem:', error);
      return null;
    }
  };

  const handleSave = async () => {
    const croppedBlob = await createCroppedImage();
    if (croppedBlob) {
      const croppedFile = new File([croppedBlob], 'foto_cracha.jpg', {
        type: 'image/jpeg',
      });
      onSave(croppedFile);
    }
    onClose();
  };

  const handleCancel = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth='sm'
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '600px',
        },
      }}
    >
      <DialogTitle>
        Ajustar Foto do Crachá
        <Typography variant='caption' display='block' color='text.secondary'>
          Mova e amplie a foto para enquadrar melhor
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: 400,
            backgroundColor: '#333',
            borderRadius: 2,
          }}
        >
          <Cropper
            image={imageUrl}
            crop={crop}
            zoom={zoom}
            aspect={2.1 / 3}
            cropShape='rect'
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </Box>

        <Stack spacing={2} sx={{ mt: 3 }}>
          <Typography variant='body2' color='text.secondary'>
            Zoom
          </Typography>
          <Stack direction='row' spacing={2} alignItems='center'>
            <ZoomOutIcon color='action' />
            <Slider
              value={zoom}
              min={1}
              max={3}
              step={0.1}
              onChange={(e, value) => setZoom(value)}
              sx={{ flex: 1 }}
            />
            <ZoomInIcon color='action' />
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleCancel} color='inherit'>
          Cancelar
        </Button>
        <Button onClick={handleSave} variant='contained' color='primary'>
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Função auxiliar para criar imagem a partir de URL
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

export default PhotoCropModal;
